import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { catchError, map, Observable, of, shareReplay, tap } from 'rxjs';
import { Icon } from './ICONLIB';

@Injectable({
    providedIn: 'root',
})
export class IconService {
    #iconCache: { [key: string]: SafeHtml | Observable<SafeHtml> } = {};
    #sanitizer = inject(DomSanitizer);
    #http = inject(HttpClient);

    getIcon(key: string, data: Icon): Observable<SafeHtml> {
        if (this.#svgCached(key)) {
            return of(this.#iconCache[key] as SafeHtml);
        }

        if ('svg' in data) {
            this.#cacheSvg(key, data.svg);
            return of(this.#iconCache[key] as SafeHtml);
        }

        if ('url' in data) {
            if (!this.#iconCache[key]) {
                this.#iconCache[key] = this.#loadIcon(data.url).pipe(
                    shareReplay(1),
                    tap((icon: string) => this.#cacheSvg(key, icon)),
                    map((icon: string) => this.#sanitizer.bypassSecurityTrustHtml(icon))
                );
            }
            return this.#iconCache[key] as Observable<SafeHtml>;
        }

        return of(this.#sanitizer.bypassSecurityTrustHtml(''));
    }

    #cacheSvg(key: string, svg: string): void {
        this.#iconCache[key] = this.#sanitizer.bypassSecurityTrustHtml(svg);
    }

    #svgCached(key: string): boolean {
        return this.#iconCache[key] !== undefined && !(this.#iconCache[key] instanceof Observable);
    }

    #loadIcon(path: string): Observable<string> {
        return this.#http.get(path, { responseType: 'text' }).pipe(
            catchError((err) => {
                console.error(`Error loading icon from URL: ${path}`, err);
                return of('');
            })
        );
    }
}
